import { useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Button, Stack, Tab, Tabs } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import SnackbarMessage from "components/SnackbarMessage";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Severity, SnackbarMessageProps } from "types/common-types";
import BugsTab from "./BugsTab";
import PullRequestsTab from "./PullRequestsTab";

const DeveloperDashboardPage = () => {
  const { formatMessage: t } = useIntl();
  useListTitle(t({ id: "developerDashboardLabel" }));
  const { developerDashboardService } = useGlobalContext();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarMessageProps | null>(null);
  const [confirmOpen, confirmOpenActions] = useBoolean(false);

  const handleRefresh = async () => {
    try {
      await developerDashboardService.refresh();
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const root = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
          return typeof root === "string" && root.startsWith("dashboard");
        },
      });
      setSnackbarMessage(new SnackbarMessageProps(t({ id: "dashboardRefreshTriggered" }), Severity.Success));
    } catch {
      setSnackbarMessage(new SnackbarMessageProps(t({ id: "dashboardRefreshFailed" }), Severity.Error));
    } finally {
      confirmOpenActions.setFalse();
    }
  };

  return (
    <Box mb={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Bugs" />
          <Tab label="Pull Requests" />
        </Tabs>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={confirmOpenActions.setTrue}>
          Refresh
        </Button>
      </Stack>

      {tab === 0 && <BugsTab />}
      {tab === 1 && <PullRequestsTab />}

      <ConfirmDialog
        open={confirmOpen}
        dialogTitleKey="dashboardRefreshConfirmTitle"
        contentKey="dashboardRefreshConfirmContent"
        confirmLabel="confirmButton"
        onCancel={confirmOpenActions.setFalse}
        handleConfirm={handleRefresh}
      />

      <SnackbarMessage snackbarMessage={snackbarMessage} setSnackbarMessage={setSnackbarMessage} />
    </Box>
  );
};

export default DeveloperDashboardPage;
